import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { CanEdit, useIsReadOnlyManager } from "@/shared/readOnlyManager";
import {
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useTable, usePagination, useSortBy, useRowSelect } from "react-table";
import ReactTablePagination from "@/shared/components/table/components/ReactTablePagination";
import ColumnSearchMenu from "@/shared/components/table/components/ColumnSearchMenu";
import IndeterminateCheckbox from "@/shared/components/form/IndeterminateCheckbox";
import { getUserColumns } from "./columns";
import ModalWrapper from "./ModalWrapper";
import RewardIcon from "mdi-react/PostItNoteAddIcon";
import GoalIcon from "mdi-react/BullseyeArrowIcon";
import ResendIcon from "mdi-react/AccountPlusIcon";
import DeactivateIcon from "mdi-react/CancelIcon";
import ActivateIcon from "mdi-react/RefreshIcon";
import LockIcon from "mdi-react/LockIcon";
import AccountLockIcon from "mdi-react/AccountLockIcon";
import UnlockIcon from "mdi-react/LockOpenIcon";
import PeerIcon from "mdi-react/PostItNoteAddIcon";
import axios from "axios";
import apiTableService from "@/services/apiTableService";
import { useTranslation } from "react-i18next";
import { inArray, amountToUsFormat } from "@/shared/helpers";
import { useNavigate, useSearchParams } from "react-router-dom";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import getCsvImportTypeOptions from "@/services/getCsvImportTypeOptions";
import getEntrataStatuses from "@/services/program/getEntrataStatuses";
import ReactTooltip from "react-tooltip";
import {
  readAssignedPositionPermissions,
  hasUserPermissions,
} from "@/services/program/budget";
import { Sorting } from "@/shared/apiTableHelper";
import Tooltip from '@material-ui/core/Tooltip';

const collectEmails = (users) => users.map((user) => user.email);

const QUERY_PAGE_SIZE = 10;
const QUERY_PAGE_SORTBY = [];

const AWARD_CREATE = "Award Create";
const AWARD_APPROVE_ALL = "Award Approve All";
const CAN_ACCESS_AWARD_PERMISSION = "can_access_award_permission";

const ACTIONS = [
  { name: "Reward", link: "", icon: <RewardIcon />, tooltip: "Give selected participants a reward" },
  { name: "Add Goal", link: "", icon: <GoalIcon />, tooltip: "Assign a goal plan to selected participants" },
  //{ name: "Email", link: "", icon: <MailIcon /> },TODO: add logic to check engagement settings
  { name: "Resend Invite", link: "", icon: <ResendIcon />, tooltip: "Resend the registration invite email" },
  { name: "Deactivate", link: "", icon: <DeactivateIcon />, tooltip: "Deactivate selected participants' accounts" },
  { name: "Activate", link: "", icon: <ActivateIcon />, tooltip: "Reactivate selected participants' accounts" },
  { name: "Lock", link: "", icon: <LockIcon />, tooltip: "Lock selected participants' accounts" },
  { name: "Unlock", link: "", icon: <UnlockIcon />, tooltip: "Unlock selected participants' accounts" },
  { name: "Request Reclaim", link: "", icon: <AccountLockIcon />, tooltip: "Request reclaim of previously awarded points" },
  //{ name: "Import", link: "", icon: <ImportIcon /> }, TODO: add logic to check engagement settings
  { name: "Peer Allocation", link: "", icon: <PeerIcon />, tooltip: "Allocate peer-to-peer points to selected participants" },
//   { name: "Instant Reward", link: "", icon: <RewardIcon /> },
];
const STATUS = [
  { name: "Active" },
  { name: "Deactivated" },
  { name: "Locked" },
  { name: "New" },
  { name: "Pending Activation" },
  { name: "Pending Deactivation" },
];

const defaultStatus = STATUS
  .filter((item) => item.name !== "Deactivated")
  .map((item) => item.name);

const BULK_ACTIONS = [
  "Reward",
  "Resend Invite",
  "Deactivate",
  "Activate",
  "Lock",
  "Unlock",
  "Peer Allocation",
  "Reclaim Peer Allocations",
  "Add Goal",
  "Instant Reward",
  "Order Physical Cards",
  "Request Reclaim"
];

const POINT_COLUMN_HEADERS = [
  "Peer Balance",
  "Redeemed",
  "Point Balance",
  "Points Earned",
];

const SELECTION_COLUMN = {
    id: "selection",
    minWidth: 20,
    width: 20,
    maxWidth: 20,
  Header: ({ getToggleAllPageRowsSelectedProps }) => (
    <div>
      <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
    </div>
  ),
  Cell: ({ row }) => (
    <div>
      <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
    </div>
  ),
};

// The only participant action a Read Only Manager can take.
const READ_ONLY_MANAGER_ACTIONS = ["Resend Invite"];

const PENDING_ACTION = {
  name: "Pending",
  link: "",
  icon: <RewardIcon />,
  tooltip: "An award is awaiting your approval for this participant",
};

const getVisibleParticipantActions = (actions, participant) => {
  const currentStatus = participant?.status?.status ?? null;
  // ICOV3-1701: the row's Reward action becomes Pending when this participant
  // has a request awaiting the CURRENT manager specifically — requests
  // assigned to another approver leave this row's Reward button untouched.
  const isAwaitingCurrentManager = Boolean(
    participant?.budgetCascadingApproval?.awaiting_current_manager
  );

  return actions
    .filter((item) => {
      if (item.name === "Deactivate") {
        return currentStatus !== "Deactivated" && currentStatus !== null;
      }
      if (item.name === "Activate") {
        return currentStatus !== "Active" && currentStatus !== null;
      }
      if (item.name === "Lock") {
        return currentStatus !== "Locked" && currentStatus !== "Deactivated" && currentStatus !== null;
      }
      if (item.name === "Unlock") {
        return currentStatus === "Locked";
      }
      return true;
    })
    .map((item) => (item.name === "Reward" && isAwaitingCurrentManager ? PENDING_ACTION : item));
};

const RenderActions = ({ actions, row, onClickActionCb }) => {
  const visibleActions = getVisibleParticipantActions(actions, row.original);
  const primaryAction = visibleActions.find(
    (item) => item.name === "Reward" || item.name === "Pending"
  );
  const menuActions = visibleActions.filter(
    (item) => item.name !== "Reward" && item.name !== "Pending"
  );

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  if (!primaryAction && menuActions.length === 0) {
    return null;
  }

  return (
    <div className="participants-table__row-actions">
      {primaryAction && (
        <button
          type="button"
          data-tip={primaryAction.tooltip}
          className={`participants-table__row-action-btn ${
            primaryAction.name === "Pending"
              ? "participants-table__row-action-btn--pending"
              : "participants-table__row-action-btn--reward"
          }`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClickActionCb(
              primaryAction.name === "Pending" ? "Pending Approval" : "Row Reward",
              row.original
            );
          }}
        >
          {primaryAction.name}
        </button>
      )}

      {menuActions.length > 0 && (
        <UncontrolledDropdown
          className="participants-table__row-menu"
          onToggle={() => ReactTooltip.rebuild()}
        >
          <DropdownToggle caret={false} className="participants-table__row-menu-toggle">
            <MoreVertIcon fontSize="small" />
          </DropdownToggle>
          <DropdownMenu end container="body" className="participants-table__row-menu-list">
            {menuActions.map((item, index) => (
              <DropdownItem
                key={`participant-row-action-${row.original.id}-${index}`}
                tag="button"
                type="button"
                className="participants-table__row-menu-item"
                data-tip={item.tooltip}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  ReactTooltip.hide();
                  onClickActionCb(item.name, row.original);
                }}
              >
                <span className={`participants-table__row-menu-icon ${item.name}`}>
                  {item.icon}
                </span>
                <span>{item.name}</span>
              </DropdownItem>
            ))}
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
    </div>
  );
};

const ProgramParticipants = ({ auth, rootProgram, program, organization, balance, refreshBalance, refreshApprovals }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const readOnly = useIsReadOnlyManager();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortSyncMounted = useRef(false);
  const [modalName, setModalName] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const [refreshUsers, setRefreshUsers] = useState(false);
  const [users, setUsers] = useState(null);
  const [action, setAction] = useState("");
  const [queryPageSize, setQueryPageSize] = useState(QUERY_PAGE_SIZE);
  const [queryPageSortBy, setQueryPageSortBy] = useState(() => {
    const col = searchParams.get("sortby");
    const dir = searchParams.get("direction");
    return col ? [{ id: col, desc: dir === "desc" }] : QUERY_PAGE_SORTBY;
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ keyword: "", status: defaultStatus, search_in_all: false, search_columns: "" });
  const [participants, setParticipants] = useState([]);
  const [importtypeExists, setImporttypeExists] = useState(false);
  const [assignedPermissions, setAssignedPermissions] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [entrataStatuses, setEntrataStatuses] = useState([]);
  // ICOV3-1701: visibility gate for the Pending Approvals filter button — a
  // manager can be on an approval-enabled program without being an approver
  // themselves, so this can't be inferred from rootProgram.use_cascading_approvals
  // alone. Independent fetch of the same endpoint BudgetAndApprovalsSummary
  // already calls, mirroring that existing pattern rather than lifting state up.
  const [approvalPermissions, setApprovalPermissions] = useState({
    program_approval_enabled: false,
    authorized_approver: false,
  });
  const isPayInAdvance = program ? (program.is_pay_in_advance === 1 || program.is_pay_in_advance === true) : false;
  const availableActions = useMemo(
    () =>
      ACTIONS.filter((item) => {
        if (readOnly) {
          return READ_ONLY_MANAGER_ACTIONS.includes(item.name);
        }
        if (item.name === "Peer Allocation") {
          return Boolean(program?.uses_peer2peer);
        }
        if (item.name === "Add Goal") {
          return Boolean(program?.uses_goal_tracker);
        }
        return true;
      }),
    [program?.uses_goal_tracker, program?.uses_peer2peer, readOnly]
  );

  const availableBulkActions = useMemo(
    () =>
      BULK_ACTIONS.filter((item) => {
        if (item === "Peer Allocation" || item === "Reclaim Peer Allocations") {
          return Boolean(program?.uses_peer2peer);
        }
        if (item === "Add Goal") {
          return Boolean(program?.uses_goal_tracker);
        }
        return true;
      }),
    [program?.uses_goal_tracker, program?.uses_peer2peer]
  );

  const searchableColumns = useMemo(() => {
    const cols = [
      { key: "first_name", label: "First Name" },
      { key: "last_name", label: "Last Name" },
      { key: "email", label: "Email" },
      { key: "employee_number", label: "Employee ID" },
    ];
    if (program?.is_cocard_program) {
      cols.push({ key: "merchant", label: "Merchants" });
    }
    if (program?.uses_units) {
      cols.push({ key: "unit", label: "Unit" });
    }
    if (program?.has_entrata_hierarchy) {
      cols.push({ key: "lease_id", label: "Lease ID" });
      cols.push({ key: "entrata_status", label: "Entrata Status" });
    }
    return cols;
  }, [program]);

  const sortableColumns = useMemo(() => {
    return getUserColumns(program, { searchInAll: filter.search_in_all })
      .filter(col => col.accessor && col.Header)
      .map(col => ({ key: col.accessor, label: col.Header }));
  }, [program, filter.search_in_all]);

  useEffect(() => {
    if (program?.id) {
      getCsvImportTypeOptions(program.organization_id, program.id, { checkOnly: 1 })
        .then((res) => {
          setImporttypeExists(Boolean(res?.exists));
        })
        .catch((error) => {
          console.log(error);
          setImporttypeExists(false);
        });
    }
  }, [program]);

  useEffect(() => {
    if (program?.has_entrata_hierarchy) {
      getEntrataStatuses(organization.id, program.id)
        .then((statuses) => {
          const mapped = (statuses || [])
            .filter((s) => s !== "Past")
            .map((s) => ({ name: s }));
          setEntrataStatuses(mapped);
        })
        .catch(() => {
          setEntrataStatuses([]);
        });
    }
  }, [organization.id, program.id, program?.has_entrata_hierarchy]);

  useEffect(() => {
    let current = true;
    if (organization?.id && program?.id) {
      axios
        .get(`/organization/${organization.id}/program/${program.id}/manager-approval-pending-count`)
        .then((response) => {
          if (!current) return; // a newer fetch superseded this one — don't overwrite with a stale result
          setApprovalPermissions({
            program_approval_enabled: Boolean(response.data?.program_approval_enabled),
            authorized_approver: Boolean(response.data?.authorized_approver),
          });
        })
        .catch(() => {
          if (current) setApprovalPermissions({ program_approval_enabled: false, authorized_approver: false });
        });
    }
    return () => { current = false; };
  }, [organization?.id, program?.id, refreshUsers]);

  // ICOV3-1701: if the newly-selected program doesn't have approval enabled
  // or this manager isn't an authorized approver there, the filter button
  // itself disappears (see above) — also clear an active filter so a stale
  // "pending only" view doesn't silently persist into a program where it no
  // longer applies.
  useEffect(() => {
    if (!approvalPermissions.program_approval_enabled || !approvalPermissions.authorized_approver) {
      setFilter((prev) => {
        if (!prev.pending_for_manager) return prev;
        const next = { ...prev };
        delete next.pending_for_manager;
        return next;
      });
    }
  }, [approvalPermissions.program_approval_enabled, approvalPermissions.authorized_approver]);

  useEffect(() => {
    if (organization.id && rootProgram.id && auth?.positionLevel?.id) {
      //setLoading(true);
      readAssignedPositionPermissions(
        organization.id,
        rootProgram?.id,
        auth?.positionLevel?.id
      )
        .then((res) => {
          setAssignedPermissions(res);
          //setLoading(false);
        })
        .catch((e) => {
          console.log(e);
          //setLoading(false);
        });
    }
  }, [organization, program, auth]);

  const cleanupModalArtifacts = useCallback(() => {
    if (typeof document === "undefined") return;
    if (document.querySelector(".modal.show")) return;

    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
  }, []);

  const toggle = useCallback((name = null) => {
    if (typeof name === "string") {
      setModalName(name);
      setOpen(true);
      return;
    }

    setOpen(false);
    setModalName(null);
    setAction("");
  }, []);

  const doAction = useCallback((nextAction, nextParticipants) => {
    if (nextAction === "Email") {
      const emails = collectEmails(nextParticipants);
      window.location.href = `mailto:${emails.join(
        ","
      )}?subject=You have received a message!`;
    }
    if (inArray(nextAction, availableBulkActions)) {
      toggle(nextAction);
    }
  }, [availableBulkActions, toggle]);

  useEffect(() => {
    if (action && participants) {
      doAction(action, participants);
    }
  }, [action, doAction, participants]);

  useEffect(() => {
    if (isOpen) return undefined;

    const timeoutId = setTimeout(() => {
      cleanupModalArtifacts();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [cleanupModalArtifacts, isOpen]);

  const onClickAction = useCallback((name, row) => {
    if (name == "Name" || name === "First Name" || name === "Last Name") {
      setParticipants(row);
    } else if (name == "Participant Award Revoke" || name === "Pending Approval") {
      setParticipants(row);
    } else {
      setParticipants([row]);
    }
    toggle(name === "First Name" || name === "Last Name" ? "Name" : name);
  }, [toggle]);

  const onSelectAction = (name) => {
    const rows = selectedFlatRows.map((d) => d.original);
    const skipSelectionCheck = ['Instant Reward', 'Order Physical Cards'];

    if (rows.length === 0 && !skipSelectionCheck.includes(name)) {
        alert("Select participants");
        return;
    }

    const MSGS = {
      'Deactivated': "A participant with status 'Deactivated' cannot be awarded.",
      'Locked': "A participant with status 'Locked' cannot be awarded."
    };

    const wrongRecords = new Set();

    for (const row of rows) {
      const s = row?.status?.status;
      if (s && MSGS[s]) wrongRecords.add(MSGS[s]);
    }

    if (wrongRecords.size) {
      alert([...wrongRecords].join('\n'));
      return;
    }

    if ((name === "Reward" || name === "Instant Reward") && rootProgram.use_cascading_approvals) {
      const canCreateAward = hasUserPermissions(
        assignedPermissions,
        AWARD_CREATE,
        CAN_ACCESS_AWARD_PERMISSION
      );
      const isApproveAllSuperuser = hasUserPermissions(
        assignedPermissions,
        AWARD_APPROVE_ALL,
        CAN_ACCESS_AWARD_PERMISSION
      );

      if (!canCreateAward && !isApproveAllSuperuser) {
        alert(
          "Based on permission settings, your user role is unable to complete task."
        );
        return;
      }
    }
    setAction(name);
    setParticipants(rows);
  };

  const onSelectEntry = (value) => {
    const currentPageIndex = Math.floor((pageIndex * pageSize) / value);
    setQueryPageSize(value);
    setPageSize(value);

    if (currentPageIndex >= Math.ceil(users.count / value)) {
      gotoPage(0);
    } else {
      gotoPage(currentPageIndex);
    }
  };

  const strShowName = useCallback((name, value, p) => {
    if (!value) return "";
    let displayName = value;
    return (
      <div className="d-flex align-items-center gap-2">
        {p?.budgetCascadingApproval?.count > 0 && (
          <span
            data-tip="Award Pending for Approval"
            className={`participants-table__approval-badge${readOnly ? "" : " link"}`}
            onClick={readOnly ? undefined : () => onClickAction("Participant Award Revoke", p)}
          >
            {p.budgetCascadingApproval.count}
          </span>
        )}
        {/* {Number(p?.is_locked) === 1 && (
            <span className="action-item hover-text text-danger">
                <a href={SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tip="Request unlock"
                data-for={`unlock-tip-${p.id}`}
                className="text-danger d-flex align-items-center"
                style={{ textDecoration: "none" }}
                >
                    <UnlockIcon size={24} />
                </a>
                <div className="tooltip-text">Request unlock</div>
            </span>
        )} */}
        <span onClick={() => onClickAction(name, p)} className={"link"}>
          {displayName}
        </span>
      </div> // todo add count of awaiting reward
    );
  }, [onClickAction, readOnly]);

  // Read Only Managers keep the selection checkboxes and Action column, limited to Resend Invite via availableActions.
  const columns = useMemo(() => ([
    SELECTION_COLUMN,
    ...getUserColumns(program, { searchInAll: filter.search_in_all }).map((col) => {
      if (col.Header === "First Name" || col.Header === "Last Name") {
        return {
          ...col,
          Cell: ({ row, value }) => strShowName(col.Header, value, row.original),
        };
      }
      if (inArray(col.Header, POINT_COLUMN_HEADERS)) {
        return {
          ...col,
          Cell: ({ value }) =>
            `${amountToUsFormat(value * program.factor_valuation, false)}`,
        };
      }
      return col;
    }),
    {
      Header: "Action",
      accessor: "action",
      minWidth: 132,
      width: 132,
      maxWidth: 148,
      Cell: ({ row }) => (
        <RenderActions
          actions={availableActions}
          row={row}
          onClickActionCb={onClickAction}
        />
      ),
    },
  ]), [availableActions, onClickAction, program, strShowName, filter.search_in_all]);

const TableSkeleton = ({ rows = 10, cols = 5 }) => (
  <>
    {[...Array(rows)].map((_, i) => (
      <tr key={`skel-row-${i}`}>
        {[...Array(cols)].map((_, j) => (
          <td key={`skel-col-${j}`}>
            <div className="skeleton-line" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

  const tableData = useMemo(() => (users ? users.results : []), [users]);

  const tableInstance = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        pageIndex: 0,
        pageSize: queryPageSize,
        hiddenColumns: !program.uses_peer2peer ? ["peerBalance"] : [],
        sortBy: queryPageSortBy,
      },
      manualPagination: true,
      manualSortBy: true,
      pageCount: Math.ceil(users?.count / queryPageSize),
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    selectedFlatRows,
    setSortBy: tableSetSortBy,
    state: { pageIndex, pageSize, sortBy },
  } = tableInstance;

  useEffect(() => {
    const timeoutId = setTimeout(() => ReactTooltip.rebuild(), 0);
    return () => clearTimeout(timeoutId);
  }, [users, columns, availableActions]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    apiTableService
      .fetchData({
        url: `/organization/${organization.id}/program/${program.id}/participant-sort`,
        page: pageIndex,
        size: queryPageSize,
        sortby: queryPageSortBy,
        filter,
      })
      .then((items) => {
        if (mounted) {
          setUsers(items);
          if (isOpen && modalName == "Name") {
            let user = items.results.find((user) => user.id == participants.id);
            setParticipants(user);
          }
          setLoading(false);
        }
      }).catch(() => {
        if (mounted) setLoading(false);
      });
    return () => (mounted = false);
  }, [filter, organization.id, pageIndex, program.id, queryPageSize, queryPageSortBy]);

  useEffect(() => {
    if (refreshUsers) {
      setRefreshUsers(false);
      setFilter((prev) => ({ ...prev, status: defaultStatus }));
    }
  }, [refreshUsers]);

  useEffect(() => {
    setQueryPageSortBy(sortBy);
    gotoPage(0);
    if (!sortSyncMounted.current) {
      sortSyncMounted.current = true;
      return;
    }
    setSearchParams((prev) => {
      if (sortBy.length > 0) {
        prev.set("sortby", sortBy[0].id);
        prev.set("direction", sortBy[0].desc ? "desc" : "asc");
      } else {
        prev.delete("sortby");
        prev.delete("direction");
      }
      return prev;
    }, { replace: true });
  }, [sortBy, gotoPage]); // setSearchParams is stable (Router v6)

  const manualPageSize = [];

  const UserTable = () => {
    const colCount = headerGroups[0]?.headers?.length || 6;
    return (
      <div className="participants-table__scroll">
        <Table className="participants-table__table" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    title={undefined}
                    style={{ width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth }}
                  >
                    <span data-tip={column.tooltip} style={{ display: "inline-block" }}>
                      {column.render('Header')}
                    </span>
                    {column.isSorted ? <Sorting column={column} /> : ''}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {(loading && !users?.results?.length > 0 )? (
                <TableSkeleton rows={queryPageSize} cols={colCount} />
            ) : users?.results?.length > 0 ? (
            rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} data-label={typeof cell.column.Header === 'string' ? cell.column.Header : ''}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })) : (
              <tr>
                <td className="participants-table__empty" colSpan={columns.length}>
                  No participants found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  const ActionsDropdown = () => {
    return (
      <UncontrolledDropdown
        className="participants-table__actions-dropdown"
        onToggle={() => ReactTooltip.rebuild()}
      >
        <DropdownToggle caret className="participants-table__actions-toggle">
          Actions
        </DropdownToggle>
        <DropdownMenu className="participants-table__actions-menu">
          {availableActions.map((item, index) => (
            <DropdownItem
              key={`action-dropdown-item-${index}`}
              className="participants-table__actions-item"
              data-tip={item.tooltip}
              onClick={() => {
                ReactTooltip.hide();
                onSelectAction(item.name);
              }}
            >
              <span className="participants-table__actions-icon">{item.icon}</span>
              <span>{item.name}</span>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };



  return (
    <>
      <div className="participants-table">
        <div className={`participants-table__toolbar${!isPanelOpen ? " is-closed" : ""}`}>
          <div className="participants-table__toolbar-left">
            <ActionsDropdown />
            <CanEdit>
              {importtypeExists && (
                <button
                  type="button"
                  data-tip="Bulk import participants from a CSV file"
                  className="participants-table__toolbar-btn"
                  onClick={() => navigate("/manager/csv-import")}
                >
                  Import CSV
                </button>
              )}
            </CanEdit>
            <CanEdit>
              <div className="w-sm-100 mb-3 mb-lg-0 mb-md-0 mb-xl-0" style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                  <Tooltip
                      title={
                          <div style={{ fontSize: "13px", lineHeight: "1.6", padding: "4px" }}>
                          <strong>Instant Reward</strong>
                          <p style={{ margin: "6px 0 0" }}>
                              Send rewards instantly to one or more participants:
                          </p>
                          <ul style={{ margin: "4px 0 0", paddingLeft: "16px" }}>
                              <li>Reward existing participants or add new ones</li>
                              <li>Set a custom amount per recipient</li>
                              <li>Choose an award event</li>
                              <li>Add a personal message</li>
                          </ul>
                          <p style={{ margin: "6px 0 0" }}>
                              Recipients receive an email notification and can redeem their reward immediately.
                          </p>
                          </div>
                      }
                      classes={{ tooltip: "app-tooltip--mui", arrow: "app-tooltip--mui-arrow" }}
                      arrow
                      placement="right"
                      interactive
                      enterTouchDelay={0}
                      leaveTouchDelay={5000}
                      >
                          <button
                              color="primary"
                              className="participants-table__toolbar-btn btn-instant-reward"
                              onClick={() => {
                              onSelectAction('Instant Reward');
                              }}
                          >
                              Send Rewards
                          </button>
                  </Tooltip>
                  {isPayInAdvance && (
                      <button
                      color="info"
                      data-tip="Order physical reward cards for selected participants"
                      className="participants-table__toolbar-btn btn-order-physical-cards"
                      onClick={() => {
                          setAction('Order Physical Cards');
                      }}
                      >
                      Order Physical Cards
                  </button>
                  )}
                  </div>
            </CanEdit>

            <label className="participants-table__toggle-label">
              <input
                type="checkbox"
                className="participants-table__toggle-check"
                checked={!!filter.search_in_all}
                onChange={() =>
                  setFilter(prev => {
                    const next = { ...prev };
                    if (prev.search_in_all) {
                      delete next.search_in_all;
                    } else {
                      next.search_in_all = true;
                    }
                    return next;
                  })
                }
              />
              <span
                data-tip="Selecting this option displays participants for all programs"
                data-for="participants-all-programs-tooltip"
              >
                All Programs
              </span>
            </label>
            <ReactTooltip
              id="participants-all-programs-tooltip"
              place="top"
              type="dark"
              effect="solid"
            />
            {approvalPermissions.program_approval_enabled && approvalPermissions.authorized_approver && (
              <button
                type="button"
                data-tip="Show only participants with an award awaiting your approval"
                className={`participants-table__toolbar-btn participants-table__toolbar-btn--filter${
                  filter.pending_for_manager ? " is-active" : ""
                }`}
                aria-pressed={Boolean(filter.pending_for_manager)}
                onClick={() =>
                  setFilter((prev) => {
                    const next = { ...prev };
                    if (prev.pending_for_manager) {
                      delete next.pending_for_manager;
                    } else {
                      next.pending_for_manager = 1;
                    }
                    return next;
                  })
                }
              >
                Pending Approvals
              </button>
            )}
            {Boolean(program?.user_has_to_claim_award) && (
              <button
                type="button"
                data-tip="View rewards that have not yet been claimed"
                className="participants-table__toolbar-btn participants-table__toolbar-btn--accent"
                onClick={() => navigate("/manager/unclaimed-awards")}
              >
                Unclaimed Rewards
              </button>
            )}
          </div>
          <div className="participants-table__toolbar-right">
            <ColumnSearchMenu
              columns={searchableColumns}
              sortColumns={sortableColumns}
              statuses={STATUS}
              defaultStatuses={defaultStatus}
              entrataStatuses={entrataStatuses}
              filter={filter}
              setFilter={setFilter}
              sortBy={sortBy}
              onSort={(columnId, desc) => {
                tableSetSortBy(columnId ? [{ id: columnId, desc }] : []);
              }}
              disclosurePortalId="csm-disclosure-portal"
              onPanelToggle={setIsPanelOpen}
            />
          </div>
        </div>
        <div id="csm-disclosure-portal"></div>
        <UserTable />
        {rows.length > 0 && (
          <div className="participants-table__pagination">
            <ReactTablePagination
              page={page}
              gotoPage={gotoPage}
              previousPage={previousPage}
              nextPage={nextPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageSize={pageSize}
              pageIndex={pageIndex}
              pageCount={pageCount}
              setPageSize={onSelectEntry}
              manualPageSize={manualPageSize}
              dataLength={users ? users.count : 0}
              showFirstPage
              showLastPage
              showJumpToPage
            />
          </div>
        )}
      </div>
      <ModalWrapper
        name={modalName}
        setRefreshUsers={setRefreshUsers}
        isOpen={isOpen}
        setOpen={setOpen}
        toggle={toggle}
        participants={participants}
        balance={balance}
        program={program}
        organization={organization}
        auth={auth}
        refreshBalance={refreshBalance}
        refreshApprovals={refreshApprovals}
      />
      {/* <pre>
            <code>
                {JSON.stringify(
                {
                    selectedRowIds: selectedRowIds,
                    'selectedFlatRows[].original': selectedFlatRows.map(
                    d => d.original
                    ),
                },
                null,
                2
                )}
            </code>
            </pre> */}
    </>
  );
};

export default ProgramParticipants;
